import React, { useState, useEffect } from "react";
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Zap,
  ZapOff,
  SwitchCamera,
} from "lucide-react";
import home from "../../Assets/Camera/camera.png";
import logo from "../../Assets/Camera/Logo.svg";
import sparkle from "../../Assets/Camera/Sparkle.svg";
import snapchatLogo from "../../Assets/Camera/snapchat.png"; // Import the Snapchat logo

import "./Camera.css";

export const CameraControls = ({
  flipCamera,
  isMicOn,
  setIsMicOn,
  sessionRef,
  mediaStreamRef,
  className,
}) => {
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const checkMobileDevice = () => {
      const isMobile =
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      setIsMobileDevice(isMobile);
    };
    checkMobileDevice();
    window.addEventListener("resize", checkMobileDevice);
    return () => window.removeEventListener("resize", checkMobileDevice);
  }, []);

  const handleButtonClick = (setter, action) => {
    setter((prev) => {
      const newState = !prev;
      if (action) action(newState);
      return newState;
    });
  };

  const handleFlashToggle = async () => {
    if (mediaStreamRef.current) {
      const videoTrack = mediaStreamRef.current.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities();
      if (capabilities.torch) {
        try {
          await videoTrack.applyConstraints({
            advanced: [{ torch: !isFlashOn }],
          });
          setIsFlashOn((prev) => !prev);
        } catch (error) {
          console.error("Error toggling torch:", error);
        }
      } else {
        console.warn("Torch is not supported on this device.");
      }
    }
  };

  const handleFlipCamera = () => {
    setIsFlipped((prev) => !prev);
    flipCamera();
  };

  return (
    <div className={`right-controls ${className}`}>
      {isMobileDevice && (
        <button
          onClick={handleFlipCamera}
          className="w-12 h-12 bg-transparent rounded-lg flex items-center justify-center hover:bg-black/20 transition-all duration-300"
          style={{ transform: isFlipped ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <SwitchCamera className="w-6 h-6 text-white" />
        </button>
      )}
      <button
        onClick={() => handleButtonClick(setIsMicOn)}
        className="w-12 h-12 bg-transparent rounded-lg flex items-center justify-center hover:bg-black/20 transition-transform active:scale-90"
      >
        {isMicOn ? (
          <Mic className="w-6 h-6 text-white" />
        ) : (
          <MicOff className="w-6 h-6 text-white" />
        )}
      </button>
      <button
        onClick={() =>
          handleButtonClick(setIsSoundOn, (isSoundOn) => {
            if (sessionRef.current) {
              if (isSoundOn) sessionRef.current.unmute(false);
              else sessionRef.current.mute(false);
            }
          })
        }
        className="w-12 h-12 bg-transparent rounded-lg flex items-center justify-center hover:bg-black/20 transition-transform active:scale-90"
      >
        {isSoundOn ? (
          <Volume2 className="w-6 h-6 text-white" />
        ) : (
          <VolumeX className="w-6 h-6 text-white" />
        )}
      </button>
      {isMobileDevice && (
        <button
          onClick={handleFlashToggle}
          className="w-12 h-12 bg-transparent rounded-lg flex items-center justify-center hover:bg-black/20 transition-all duration-300"
          style={{
            transform: !isFlipped ? "scale(0)" : "scale(1)",
            opacity: !isFlipped ? 0 : 1,
          }}
        >
          {isFlashOn ? (
            <Zap className="w-6 h-6 text-white" />
          ) : (
            <ZapOff className="w-6 h-6 text-white" />
          )}
        </button>
      )}
    </div>
  );
};

export const Navbar = () => {
  useEffect(() => {
    const hamburger = document.getElementById("hamburger");
    const mobileMenu = document.getElementById("mobile-menu");
    const handleHamburgerClick = () => {
      hamburger.classList.toggle("active");
      mobileMenu.classList.toggle("active");
      document.body.style.overflow = mobileMenu.classList.contains("active")
        ? "hidden"
        : "auto";
    };
    hamburger.addEventListener("click", handleHamburgerClick);
    return () => hamburger.removeEventListener("click", handleHamburgerClick);
  }, []);

  return (
    <nav className="nbar">
      <div className="nbar-background"></div>
      <a
        href="https://kunalraj.dev"
        onClick={(e) => {
          e.preventDefault();
          navigator.clipboard.writeText("https://kunalraj.dev");
          alert("Link to this website has been to your clipboard!");
        }}
      >
        <div className="camera-logo fall">
          <img src={home} alt="" width="120%" />
          <img src={sparkle} alt="" />
        </div>
      </a>
      <div className="logo">
        <a href="https://kunalraj.dev/">
          <img src={logo} alt="Augment.ing Logo" width="160" />
        </a>
      </div>
      
    </nav>
  );
};

export const PrivacyPopup = ({ onAgree }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const hasAgreed = localStorage.getItem("privacyAgreed");
    if (hasAgreed) {
      document.body.classList.add("privacy-agreed"); // Trigger animations
      setTimeout(() => {
        onAgree();
      }, 500); // Delay to match animation timing
    }
  }, [onAgree]);

  const handleAgree = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setFadeOut(true); // Trigger fade-out effect
      document.body.classList.add("privacy-agreed"); // Add class to change z-index of preloader

      // Save agreement in localStorage
      localStorage.setItem("privacyAgreed", "true");

      setTimeout(() => {
        onAgree();
      }, 500); // Delay of 500ms before calling onAgree
    } catch (error) {
      console.error(
        "Permission denied or error accessing media devices:",
        error
      );
      alert(
        "Unable to access camera and microphone. Please check your permissions."
      );
    }
  };

  return (
    <>
      <div className={`privacy-popup ${fadeOut ? "fade-out" : ""}`}>
        <div className="privacy-popup-content">
          <img
            src={snapchatLogo}
            alt="Snapchat Logo"
            className="snapchat-logo"
          />
          <div className="privacy-links">
            <h2>Privacy Notice</h2>
            <p>
              By using Lenses, you acknowledge and agree to Snap’s{" "}
              <a
                href="https://values.snap.com/privacy/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              ,{" "}
              <a
                href="https://snap.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              , and consent to the use of your camera and microphone for
              recording purposes. Some Lenses use information about your face,
              hands, and voice to work. To learn more, tap{" "}
              <a
                href="https://help.snapchat.com/hc/en-us/articles/7012366118676"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn More
              </a>
            </p>
            If you agree and wish to continue, tap the button below.
          </div>
          <button onClick={handleAgree}>Agree</button>
        </div>
      </div>
    </>
  );
};
